import React, { useState, useEffect } from 'react';
import { materialsAPI, carbonAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Slider } from '../components/ui/slider';
import { Calculator, Plus, Trash2, Leaf, RefreshCw, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const CarbonCalculator = () => {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  const [selectedMaterials, setSelectedMaterials] = useState([
    { material_id: '', quantity: 1000, unit: 'kg' },
  ]);

  const [result, setResult] = useState(null);

  const COLORS = ['#0F2818', '#800020', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const [materialsRes, categoriesRes] = await Promise.all([
        materialsAPI.getAll(),
        materialsAPI.getCategories(),
      ]);
      setMaterials(materialsRes.data.materials);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const filteredMaterials = selectedCategory === 'all'
    ? materials
    : materials.filter((m) => m.category === selectedCategory);

  const handleAddMaterial = () => {
    setSelectedMaterials([...selectedMaterials, { material_id: '', quantity: 1000, unit: 'kg' }]);
  };

  const handleRemoveMaterial = (index) => {
    if (selectedMaterials.length === 1) return;
    setSelectedMaterials(selectedMaterials.filter((_, i) => i !== index));
  };

  const handleMaterialChange = (index, field, value) => {
    const newMaterials = [...selectedMaterials];
    newMaterials[index][field] = value;
    setSelectedMaterials(newMaterials);
  };

  const handleCalculate = async () => {
    const validMaterials = selectedMaterials.filter((m) => m.material_id && m.quantity > 0);
    if (validMaterials.length === 0) {
      toast.error('Please add at least one material with quantity');
      return;
    }

    setCalculating(true);
    try {
      const response = await carbonAPI.calculate(validMaterials);
      setResult(response.data);
      toast.success('Carbon calculated successfully');
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setSelectedMaterials([{ material_id: '', quantity: 1000, unit: 'kg' }]);
    setResult(null);
  };

  const getMaterialInfo = (materialId) => {
    return materials.find((m) => m.id === materialId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#0F2818] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const categoryChartData = result
    ? Object.entries(result.carbon_by_category).map(([name, value]) => ({
        name,
        value: Math.round(value),
      }))
    : [];

  return (
    <div className="p-8" data-testid="carbon-calculator-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Carbon Calculator
            </h1>
            <p className="text-slate-600 mt-1">
              Calculate embodied carbon using UK ICE database factors
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset} data-testid="reset-button">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              className="bg-[#0F2818] hover:bg-[#06140C]"
              onClick={handleCalculate}
              disabled={calculating}
              data-testid="calculate-button"
            >
              {calculating ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Calculating...
                </span>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Carbon
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Materials Input */}
        <div className="space-y-6">
          <Card className="border-slate-200" data-testid="materials-input-card">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Materials
                </CardTitle>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-40" data-testid="category-filter">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {selectedMaterials.map((mat, index) => {
                const materialInfo = getMaterialInfo(mat.material_id);
                return (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-4" data-testid={`material-row-${index}`}>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Material {index + 1}</Label>
                      {selectedMaterials.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMaterial(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <Select
                      value={mat.material_id}
                      onValueChange={(value) => handleMaterialChange(index, 'material_id', value)}
                    >
                      <SelectTrigger data-testid={`material-select-${index}`}>
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredMaterials.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{m.name}</span>
                              <span className="text-xs text-slate-500 ml-2">
                                {m.carbon_factor} {m.unit}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {materialInfo && (
                      <div className="flex items-center gap-2 text-xs text-slate-500 bg-white px-3 py-2 rounded">
                        <Info className="w-3 h-3" />
                        <span>
                          Carbon factor: <span className="font-mono font-medium">{materialInfo.carbon_factor}</span> {materialInfo.unit}
                        </span>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Quantity (kg)</Label>
                        <span className="font-mono text-sm font-medium text-[#0F2818]">
                          {mat.quantity.toLocaleString()} kg
                        </span>
                      </div>
                      <Slider
                        value={[mat.quantity]}
                        onValueChange={([value]) => handleMaterialChange(index, 'quantity', value)}
                        max={50000}
                        min={100}
                        step={100}
                        className="w-full"
                        data-testid={`quantity-slider-${index}`}
                      />
                      <Input
                        type="number"
                        value={mat.quantity}
                        onChange={(e) => handleMaterialChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="mt-2"
                        data-testid={`quantity-input-${index}`}
                      />
                    </div>

                    {materialInfo && mat.quantity > 0 && (
                      <div className="text-right">
                        <span className="text-sm text-slate-600">Estimated carbon: </span>
                        <span className="font-mono font-semibold text-[#0F2818]">
                          {((mat.quantity * materialInfo.carbon_factor) / 1000).toFixed(2)} tCO2e
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              <Button
                variant="outline"
                className="w-full"
                onClick={handleAddMaterial}
                data-testid="add-material-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Material
              </Button>
            </CardContent>
          </Card>

          {/* ICE Database Info */}
          <Card className="border-slate-200 bg-[#E8EEEA]" data-testid="ice-database-info">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#0F2818] flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    ICE Database v3.0
                  </h4>
                  <p className="text-sm text-slate-600">
                    Carbon factors based on the UK's Inventory of Carbon & Energy database, 
                    the industry standard for embodied carbon calculations in UK construction.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="space-y-6">
          {result ? (
            <>
              {/* Total Carbon */}
              <Card className="border-slate-200 border-l-4 border-l-[#0F2818]" data-testid="result-total-carbon">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider font-mono mb-1">
                        Total Embodied Carbon
                      </p>
                      <p className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {(result.total_carbon / 1000).toFixed(2)}
                        <span className="text-lg font-normal text-slate-500 ml-2">tCO2e</span>
                      </p>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-[#E8EEEA] flex items-center justify-center">
                      <Leaf className="w-8 h-8 text-[#0F2818]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Category Breakdown Chart */}
              <Card className="border-slate-200" data-testid="result-category-chart">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Carbon by Category
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {categoryChartData.length > 0 && (
                    <div className="flex items-center">
                      <ResponsiveContainer width="50%" height={200}>
                        <PieChart>
                          <Pie
                            data={categoryChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            dataKey="value"
                            paddingAngle={2}
                          >
                            {categoryChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                            contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="w-1/2 pl-4">
                        <ul className="space-y-2">
                          {categoryChartData.map((item, index) => (
                            <li key={item.name} className="flex items-center gap-2 text-sm">
                              <span
                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                              <span className="text-slate-600 truncate">{item.name}</span>
                              <span className="ml-auto font-mono text-slate-900">
                                {(item.value / 1000).toFixed(2)}t
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Material Breakdown Table */}
              <Card className="border-slate-200" data-testid="result-materials-table">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Material Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Material</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Factor</TableHead>
                        <TableHead className="text-right">Carbon</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.carbon_by_material.map((mat, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{mat.name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {mat.quantity?.toLocaleString()} {mat.unit}
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-500">
                            {mat.carbon_factor}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium text-[#0F2818]">
                            {(mat.embodied_carbon / 1000).toFixed(2)} t
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-slate-200 border-dashed" data-testid="no-result-placeholder">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <Calculator className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Ready to Calculate
                </h3>
                <p className="text-slate-600 text-center max-w-sm">
                  Select materials and quantities, then click "Calculate Carbon" to see 
                  embodied carbon results using UK ICE database factors.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarbonCalculator;
